import { Stack, Text, Group, Skeleton, Menu } from '@mantine/core';
import cn from 'clsx';
import BigNumber from 'bignumber.js';
import { formatDecimal } from 'src/utils';
import { useProtocolInfo } from 'src/api';

const SHOW_STATS = import.meta.env.VITE_SHOW_STATS === 'true';
const SHOW_APY = import.meta.env.VITE_SHOW_APY === 'true';
const SHOW_APR_BOOST = import.meta.env.VITE_SHOW_APR_BOOST === 'true';
export function Stats() {
  const { protocolInfo, isLoadingProtocolInfo } = useProtocolInfo({
    enabled: SHOW_STATS,
  });

  if (!SHOW_STATS) {
    return null;
  }

  return (
    <Group
      justify="center"
      className="self-center flex-col lg:flex-row lg:gap-0 [&>div]:w-[306px] [&>div]:px-[28px]"
    >
      <Stack
        gap={4}
        align="center"
        className="flex-grow border-r-[rgba(138,145,158,0.30)] border-[0px] lg:border-r border-solid"
      >
        <Text fz={14} fw={600} lh="20px" c="#A0A0B1">
          Capricorn TVL
        </Text>
        {isLoadingProtocolInfo || !protocolInfo || !protocolInfo?.tvl ? (
          <Skeleton w={140} h={53} />
        ) : (
          <Text fz={22} fw={600} lh={1.5} variant="gradient">
            {formatDecimal(BigNumber(protocolInfo.tvl).div(1e18), {
              shortFormat: false,
              withTruncate: true,
              maxDecimals: 2,
            })}{' '}
            MON
          </Text>
        )}
      </Stack>

      {SHOW_APY && (
        <Stack
          gap={4}
          align="center"
          className="flex-grow border-r-[rgba(138,145,158,0.30)] border-[0px] lg:border-r border-solid"
        >
          <Text fz={14} fw={600} lh="20px" c="#A0A0B1">
            APY
          </Text>
          {isLoadingProtocolInfo ||
          !protocolInfo ||
          BigNumber(protocolInfo?.apy).isNaN() ? (
            <Skeleton w={140} h={53} />
          ) : (
            <TotalApyTooltip
              apy={protocolInfo?.apy}
              boost={protocolInfo?.boost}
            >
              <Text
                fz={22}
                fw={600}
                lh={1.5}
                variant="gradient"
                className="flex items-center gap-1"
              >
                {SHOW_APR_BOOST && (
                  <img src="/boost2.svg" alt="Boost" className="w-5 h-5" />
                )}
                ~
                {formatDecimal(
                  BigNumber(protocolInfo.apy)
                    .plus(protocolInfo.boost)
                    .toFixed(),
                  { withTruncate: true, maxDecimals: 2 }
                )}
                %
              </Text>
            </TotalApyTooltip>
          )}
        </Stack>
      )}

      <Stack gap={4} align="center" className="flex-grow">
        <Text fz={14} fw={600} lh="20px" c="#A0A0B1">
          aprMON Holders
        </Text>
        {isLoadingProtocolInfo || !protocolInfo || !protocolInfo?.stakers ? (
          <Skeleton w={140} h={53} />
        ) : (
          <Text fz={22} fw={600} lh={1.5} variant="gradient">
            {formatDecimal(protocolInfo?.stakers, {
              shortFormat: false,
              withTruncate: true,
              maxDecimals: 2,
            })}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

Stats.APY = function StatsApy() {
  const { protocolInfo, isLoadingProtocolInfo } = useProtocolInfo({
    enabled: SHOW_STATS,
  });

  if (!SHOW_STATS || !SHOW_APY) return null;

  const totalAPY = BigNumber(protocolInfo?.apy ?? 0)
    .plus(protocolInfo?.boost ?? 0)
    .toFixed();

  return (
    <TotalApyTooltip apy={protocolInfo?.apy} boost={protocolInfo?.boost}>
      <Text
        component="span"
        variant="gradient"
        className={cn(
          'h-[26px] leading-[26px] px-2 flex items-center gap-1',
          'outline outline-solid outline-primarybrand/[0.24] rounded-[10px] text-[14px] font-[600]',
          isLoadingProtocolInfo && 'animate-pulse'
        )}
      >
        {SHOW_APR_BOOST && (
          <img src="/boost.svg" alt="boost" className="w-4 h-4" />
        )}
        {isLoadingProtocolInfo ||
        !protocolInfo ||
        BigNumber(protocolInfo?.apy).isNaN() ? (
          <span className="block w-20 bg-secondarybrand" />
        ) : (
          <>
            ~{formatDecimal(totalAPY, { withTruncate: true, maxDecimals: 2 })}%
            APY
          </>
        )}
      </Text>
    </TotalApyTooltip>
  );
};

function TotalApyTooltip({
  apy,
  boost,
  children,
}: {
  apy?: string | number;
  boost?: string | number;
  children: React.ReactNode;
}) {
  if (!SHOW_APR_BOOST) {
    return children;
  }

  const totalAPY = BigNumber(apy ?? 0)
    .plus(boost ?? 0)
    .toFixed();

  return (
    <Menu trigger="hover">
      <Menu.Target>{children}</Menu.Target>
      <Menu.Dropdown className="min-w-[240px] p-4 rounded-[16px] border border-solid border-[rgba(255,255,255,0.14)] bg-[#0A0B0D]">
        <div className="flex flex-col gap-2">
          <Group justify="space-between">
            <Text c="#87868C">Base APY:</Text>
            <Text c="#fff">
              ~{formatDecimal(apy ?? 0, { withTruncate: true, maxDecimals: 2 })}
              %
            </Text>
          </Group>
          <Group justify="space-between">
            <Text c="var(--mantine-color-brand-0)">$APR Boost APY:</Text>
            <Text c="var(--mantine-color-brand-0)">
              ~
              {formatDecimal(boost ?? 0, {
                withTruncate: true,
                maxDecimals: 2,
              })}
              %
            </Text>
          </Group>
          <Group justify="space-between">
            <Text c="#87868C">Total:</Text>
            <Text c="#fff">
              ~{formatDecimal(totalAPY, { withTruncate: true, maxDecimals: 2 })}
              %
            </Text>
          </Group>
        </div>
      </Menu.Dropdown>
    </Menu>
  );
}
